import {create} from 'zustand';
import {BootstrapData} from '@ui/bootstrap-data/bootstrap-data';

export interface BootstrapDataState {
  data: BootstrapData;
  setData: (data: BootstrapData | string) => void;
  mergeData: (data: Partial<BootstrapData>) => void;
}

/**
 * Translation lines by language, kept apart from the bootstrap data.
 *
 * The server no longer embeds them in every page (1.6 MB of Arabic): each
 * locale carries a versioned `lines_url` instead, fetched once and cached by
 * the browser for a year. Bootstrap data is replaced wholesale on login, so
 * the lines live here and are put back into `i18n.locales[].lines` every time
 * data is set — the rest of the app keeps reading them where it always did.
 */
const linesCache: Record<string, Record<string, string>> = {};
const linesLoading: Record<string, Promise<void> | undefined> = {};

function withCachedLines(data: BootstrapData): BootstrapData {
  if (!data?.i18n?.locales) return data;
  let changed = false;
  const locales = data.i18n.locales.map(l => {
    const has = l.lines && Object.keys(l.lines).length > 0;
    if (has) {
      linesCache[l.language] = l.lines; // embedded (old server / locale switch)
      return l;
    }
    if (linesCache[l.language]) {
      changed = true;
      return {...l, lines: linesCache[l.language]};
    }
    return l;
  });
  return changed ? {...data, i18n: {...data.i18n, locales}} : data;
}

/** Fetch the active locale's lines if the data only has their URL. */
export function loadActiveLocaleLines(): Promise<void> {
  const data = useBootstrapDataStore.getState().data;
  const active = data?.i18n?.locales?.find(l => l.language === data.i18n.active) as
    | (BootstrapData['i18n']['locales'][number] & {lines_url?: string | null})
    | undefined;
  if (!active || !active.lines_url || Object.keys(active.lines || {}).length) {
    return Promise.resolve();
  }
  const lang = active.language;
  // The page preloads this file with crossorigin="anonymous", whose credentials mode is
  // "same-origin". Asking for "omit" here did not match that preload, so the browser threw the
  // preloaded copy away and downloaded the whole lines file a second time (owner, 2026-09-28,
  // from the console). fetch's own default is "same-origin", which matches.
  linesLoading[lang] ??= fetch(active.lines_url)
    .then(r => (r.ok ? r.json() : {}))
    .then((lines: Record<string, string>) => {
      linesCache[lang] = lines;
      const current = useBootstrapDataStore.getState().data;
      useBootstrapDataStore.setState({data: withCachedLines(current)});
    })
    .catch(() => {
      // offline / blocked: the app shows the default (English) text
      linesLoading[lang] = undefined;
    });
  return linesLoading[lang]!;
}

export const useBootstrapDataStore = create<BootstrapDataState>()(set => ({
  // set bootstrap data that was provided with initial request from backend
  data:
    typeof window !== 'undefined' && window.bootstrapData
      ? withCachedLines(decodeBootstrapData(window.bootstrapData))
      : null!,
  setData: data => {
    const decodedData =
      typeof data === 'string' ? decodeBootstrapData(data) : data;
    set({data: withCachedLines(decodedData)});
    // A login can switch the language: make sure its lines arrive.
    queueMicrotask(() => void loadActiveLocaleLines());
  },
  mergeData: (partial: Partial<BootstrapData>) => {
    set(state => ({data: withCachedLines({...state.data, ...partial})}));
    queueMicrotask(() => void loadActiveLocaleLines());
  },
}));

// Every entry (the app, the chat widget…) starts the lines download at once;
// the main app also waits for it before its first render (main.tsx).
if (typeof window !== 'undefined') {
  queueMicrotask(() => void loadActiveLocaleLines());
}

export const getBootstrapData = () => useBootstrapDataStore.getState().data;
export const setBootstrapData = useBootstrapDataStore.getState().setData;
export const mergeBootstrapData = useBootstrapDataStore.getState().mergeData;

export function decodeBootstrapData(
  data: string | BootstrapData,
): BootstrapData {
  return typeof data === 'string' ? JSON.parse(data) : data;
}
